import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  ArrowRightCircle,
  ExternalLink,
  AlertTriangle,
  Factory,
  MessageCircle,
  Package,
  Wrench,
} from 'lucide-react';
import {
  assignAdminOrderManager,
  convertCrmLead,
  ensureCrmOrderTask,
  getCrmLeadTasks,
  getCrmOrderTasks,
  patchCrmLead,
  patchCrmOrderSalesStage,
  postCrmOrderActivity,
  type CrmLeadCard,
  type CrmPipelineCard,
  type CrmTask,
  type LostReason,
  type SalesStage,
  type AdminManagerBrief,
  createCrmLead,
} from '../../../api';
import { AdminOverlayPortal } from '../AdminOverlayPortal';
import { CrmActivityFeed } from './CrmActivityFeed';
import { CrmChatThread } from './CrmChatThread';
import { CrmDealAccordion } from './CrmDealAccordion';
import { hasFullAdminPowers } from '../../../adminAccess';
import { CrmDealStepRail, scrollToCrmDealStep } from './CrmDealStepRail';
import { defaultMacroOpen, salesStageUsesOpsMacro, type CrmDealMacroId } from './crmDealStagePresets';
import {
  loadCrmDrawerMacroState,
  resolveInitialDrawerMacros,
  saveCrmDrawerMacroState,
} from './crmDealDrawerState';
import { buildCrmNavigatorLine, navigatorLineColor, pickNextOpenTask } from './crmNavigatorLine';
import { CrmLostReasonSheet } from './CrmLostReasonSheet';
import { CrmStageNotifySheet } from './CrmStageNotifySheet';
import { CrmManagerGuide } from './CrmManagerGuide';
import { CrmSalesStageIntro } from './CrmSalesStageIntro';
import { CrmOrderJourneyRail } from './CrmOrderJourneyRail';
import { CrmSalesStageGroups } from './CrmSalesStageGroups';
import { CrmOrderStatusPanel } from './CrmOrderStatusPanel';
import { OrderStatusNotifySheet } from '../OrderStatusNotifySheet';
import { STATUS_NOTE_TEMPLATE } from '../../../pages/admin/adminOrdersShared';
import { journeyStep } from '../../../lib/onvizOrderJourney';
import { CrmTaskForm } from './CrmTaskForm';
import { CrmTaskRow } from './CrmTaskRow';
import { CrmScheduleMeasurementBlock } from './CrmScheduleMeasurementBlock';
import { CrmOrderBoardPanel } from './CrmOrderBoardPanel';
import { CrmOrderComposePanel } from './CrmOrderComposePanel';
import { CrmDealNextActionBar } from './CrmDealNextActionBar';
import { useAppStore } from '../../../store';
import { intakeSourceLabel } from './crmDealStrip';
import { CrmClientContactBlock } from './CrmClientContactBlock';
import { CrmDealSalesBody } from './CrmDealSalesBody';
import { parseContactFromNotes, parseLeadTitle, resolvePipelineContact } from '../../../lib/crmContact';
import { formatAdminPanelError } from '@/lib/adminApiError';
import { CrmInlineError } from './CrmInlineError';
import type { CrmAccessParams } from '../../../crmAccess';
import { canCrmFullControl, isProductionCrmRead } from '../../../crmAccess';
import { useInvalidateCrm } from '../../../hooks/crm/useCrmQueries';
import { openOrderComposeCatalog } from '../../../orderComposeStore';
import { useHaptic } from '../../../hooks/useHaptic';

type LeadProps = {
  mode: 'lead';
  lead: CrmLeadCard;
  managers?: AdminManagerBrief[];
  onNavigateMeasurement?: (id: number) => void;
  onNavigateClient360?: (userId: number) => void;
};

type OrderProps = {
  mode: 'order';
  card: CrmPipelineCard;
  crmFullControl: boolean;
  managers?: AdminManagerBrief[];
  bitrixDealId?: string | null;
  bitrixSyncError?: string | null;
  onNavigateClient360?: (userId: number) => void;
  onOpenMeasurementsForUser?: (userId: number) => void;
  canCancelOrder?: boolean;
  onCancelOrder?: (
    orderId: number,
    opts?: { notifyClient: boolean; managerNote: string },
  ) => Promise<boolean>;
};

type CommonProps = {
  access: CrmAccessParams;
  onClose: () => void;
  onUpdated: () => void;
  hapticSelection: () => void;
  hapticSuccess: () => void;
  focusTasksOnOpen?: boolean;
};

function focusTaskForm() {
  window.setTimeout(() => {
    scrollToCrmDealStep('sales');
    const root = document.getElementById('crm-deal-task-form');
    const input = root?.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      'input, select, textarea',
    );
    input?.focus({ preventScroll: true });
  }, 280);
}

export function CrmDealDrawer(props: CommonProps & (LeadProps | OrderProps)) {
  const { access, onClose, onUpdated, hapticSelection, hapticSuccess, focusTasksOnOpen = false } = props;
  const haptic = useHaptic();
  const prodRead = props.mode === 'order' && isProductionCrmRead(access);
  const crmFull = canCrmFullControl(access);
  const invalidate = useInvalidateCrm();
  const navigate = useNavigate();
  const { role, telegramId: storeTelegramId, authAdminHint, authFullAdminHint } = useAppStore();
  const canEditMeasurement = role === 'measurer' || role === 'manager' || role === 'admin' || crmFull;
  const canEditOrderCompose =
    !prodRead && (role === 'manager' || role === 'admin' || crmFull);
  const canEditOrderPricing =
    !prodRead &&
    (crmFull ||
      hasFullAdminPowers({
        role,
        telegramId: storeTelegramId,
        serverAdminPanelHint: authAdminHint,
        serverFullAdminHint: authFullAdminHint,
      }) ||
      role === 'manager');

  const isLead = props.mode === 'lead';
  const lead = isLead ? props.lead : null;
  const card = !isLead ? props.card : null;

  const initialStage: SalesStage = isLead ? lead!.sales_stage : card!.sales_stage;
  const dealStorageId = isLead ? `lead:${lead!.id}` : `order:${card!.id}`;
  const pricingDriftBadge = !isLead && Boolean(card?.order_has_pricing_drift);

  const macroDefaults = useMemo(
    () =>
      resolveInitialDrawerMacros(dealStorageId, initialStage, {
        productionRead: prodRead,
        isLead,
        forceComposeOpen: pricingDriftBadge,
      }),
    [dealStorageId, initialStage, prodRead, isLead, pricingDriftBadge],
  );

  const [activeMacro, setActiveMacro] = useState<CrmDealMacroId>(prodRead ? 'ops' : 'sales');
  const [salesOpen, setSalesOpen] = useState(macroDefaults.sales);
  const [composeOpen, setComposeOpen] = useState(macroDefaults.compose);
  const [opsOpen, setOpsOpen] = useState(macroDefaults.ops);
  const [taskGateHighlight, setTaskGateHighlight] = useState(false);
  const [stage, setStage] = useState<SalesStage>(initialStage);
  const [tasks, setTasks] = useState<CrmTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [pendingStage, setPendingStage] = useState<SalesStage | null>(null);
  const [lostPending, setLostPending] = useState(false);
  const [stageNotifyTarget, setStageNotifyTarget] = useState<SalesStage | null>(null);
  const [stageSaveBusy, setStageSaveBusy] = useState(false);
  const [stageSaveError, setStageSaveError] = useState<string | null>(null);
  const [handoffHint, setHandoffHint] = useState(false);
  const [bitrixId, setBitrixId] = useState<string | null>(
    !isLead ? props.bitrixDealId ?? null : null,
  );
  const [cancelling, setCancelling] = useState(false);
  const [cancelSheetOpen, setCancelSheetOpen] = useState(false);
  const ensuredAutoTaskRef = useRef<number | null>(null);
  const loadTasksGen = useRef(0);
  const focusTasksAppliedRef = useRef<string | null>(null);
  const stageCommitRef = useRef<SalesStage | null>(null);
  const [activityHighlight, setActivityHighlight] = useState(false);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [convertError, setConvertError] = useState<string | null>(null);

  const entityId = isLead ? lead!.id : card!.id;
  const title = isLead ? lead!.title : card!.order_number;
  const amountLabel = !isLead
    ? `${Math.round(card!.final_amount || 0).toLocaleString('ru-RU')} ₽`
    : null;

  const leadContact = isLead
    ? (() => {
        const fromNotes = parseContactFromNotes(lead!.notes);
        const fromTitle = parseLeadTitle(lead!.title);
        const name = fromNotes.name || fromTitle.name || lead!.client_name || lead!.title;
        return {
          name,
          phone: fromNotes.phone || lead!.phone || null,
          company: fromNotes.company || fromTitle.company,
          displayName: name,
        };
      })()
    : null;
  const orderContact = !isLead && card ? resolvePipelineContact(card) : null;

  const subtitle = isLead
    ? `Лид · ${intakeSourceLabel(lead!.source)}`
    : `${orderContact?.displayName || card!.user.first_name || card!.user.username || 'Клиент'}${amountLabel ? ` · ${amountLabel}` : ''}`;

  useEffect(() => {
    saveCrmDrawerMacroState(dealStorageId, { sales: salesOpen, compose: composeOpen, ops: opsOpen });
  }, [dealStorageId, salesOpen, composeOpen, opsOpen]);

  useEffect(() => {
    if (pricingDriftBadge) setComposeOpen(true);
  }, [dealStorageId, pricingDriftBadge]);

  useEffect(() => {
    const macros = resolveInitialDrawerMacros(dealStorageId, initialStage, {
      productionRead: prodRead,
      isLead,
      forceComposeOpen: pricingDriftBadge,
    });
    stageCommitRef.current = null;
    setStage(initialStage);
    setTasks([]);
    setTaskError(null);
    setPendingStage(null);
    setLostPending(false);
    setStageNotifyTarget(null);
    setStageSaveError(null);
    setStageSaveBusy(false);
    setTaskGateHighlight(false);
    setHandoffHint(false);
    setActiveMacro(prodRead ? 'ops' : 'sales');
    setSalesOpen(macros.sales);
    setComposeOpen(macros.compose);
    setOpsOpen(macros.ops);
    ensuredAutoTaskRef.current = null;
    loadTasksGen.current += 1;
    if (!isLead && props.mode === 'order') {
      setBitrixId(props.bitrixDealId ?? null);
    }
  }, [dealStorageId, prodRead, isLead, pricingDriftBadge]);

  useEffect(() => {
    if (stageNotifyTarget || pendingStage || stageSaveBusy) return;
    const serverStage = isLead ? lead?.sales_stage : card?.sales_stage;
    if (!serverStage) return;
    if (stageCommitRef.current) {
      if (serverStage === stageCommitRef.current) {
        stageCommitRef.current = null;
      }
      return;
    }
    if (serverStage !== stage) {
      setStage(serverStage);
    }
  }, [card?.sales_stage, lead?.sales_stage, isLead, stageNotifyTarget, pendingStage, stageSaveBusy, stage]);

  const loadTasks = useCallback(async () => {
    const gen = ++loadTasksGen.current;
    setLoading(true);
    setTasksError(null);
    try {
      const data = isLead ? await getCrmLeadTasks(entityId) : await getCrmOrderTasks(entityId);
      if (gen !== loadTasksGen.current) return;
      setTasks(data);
    } catch (err: unknown) {
      if (gen !== loadTasksGen.current) return;
      setTasksError(formatAdminPanelError(err, 'Не удалось загрузить задачи'));
    } finally {
      if (gen === loadTasksGen.current) setLoading(false);
    }
  }, [entityId, isLead]);

  useEffect(() => {
    if (prodRead) return;
    void loadTasks();
    const terminal =
      !isLead &&
      card &&
      (card.status === 'cancelled' || card.sales_stage === 'lost');
    if (
      !isLead &&
      card &&
      !terminal &&
      !card.has_valid_open_task &&
      ensuredAutoTaskRef.current !== card.id
    ) {
      ensuredAutoTaskRef.current = card.id;
      void ensureCrmOrderTask(card.id).then(() => loadTasks());
    }
  }, [card?.id, entityId, isLead, loadTasks, prodRead, card?.has_valid_open_task]);

  const goMacro = (id: CrmDealMacroId) => {
    setActiveMacro(id);
    scrollToCrmDealStep(id);
    if (id === 'sales') setSalesOpen(true);
    if (id === 'compose') setComposeOpen(true);
    if (id === 'ops') setOpsOpen(true);
  };

  useEffect(() => {
    if (!focusTasksOnOpen) {
      focusTasksAppliedRef.current = null;
    }
  }, [focusTasksOnOpen]);

  useEffect(() => {
    if (!focusTasksOnOpen || prodRead || loading) return;
    if (focusTasksAppliedRef.current === dealStorageId) return;
    focusTasksAppliedRef.current = dealStorageId;
    setSalesOpen(true);
    goMacro('sales');
    focusTaskForm();
  }, [focusTasksOnOpen, prodRead, loading, dealStorageId]);

  const hasOpenTask = useMemo(() => {
    if (tasks.some((t) => t.status === 'open')) return true;
    if (isLead) {
      return Boolean(lead?.has_valid_open_task || lead?.has_overdue_task);
    }
    return Boolean(
      card?.has_open_task || card?.has_valid_open_task || card?.has_overdue_task,
    );
  }, [tasks, isLead, lead, card]);

  const nextOpenTaskKind = useMemo(
    () => pickNextOpenTask(tasks)?.kind ?? null,
    [tasks],
  );

  const highlightActivityFeed = useCallback(() => {
    setActivityHighlight(true);
    setSalesOpen(true);
    goMacro('sales');
    window.setTimeout(() => {
      document.getElementById('crm-deal-activity-feed')?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 120);
    window.setTimeout(() => setActivityHighlight(false), 3000);
  }, []);

  const triggerTaskGate = (message: string, next: SalesStage) => {
    haptic.impact('heavy');
    window.setTimeout(() => haptic.impact('heavy'), 80);
    setPendingStage(next);
    setTaskError(message);
    setTaskGateHighlight(true);
    setSalesOpen(true);
    goMacro('sales');
    focusTaskForm();
  };

  const requestStageChange = (next: SalesStage) => {
    if (next === stage) return;
    hapticSelection();
    setStageSaveError(null);
    if (isLead) {
      void applyStageChange(next);
      return;
    }
    if (next === 'lost') {
      setLostPending(true);
      return;
    }
    setStageNotifyTarget(next);
  };

  const applyStageChange = async (
    next: SalesStage,
    opts?: {
      force?: boolean;
      lost?: { reason: LostReason; note: string };
      notifyClient?: boolean;
      clientMessage?: string;
    },
  ) => {
    const force = opts?.force ?? false;
    const lost = opts?.lost;
    setTaskError(null);
    setTaskGateHighlight(false);
    setStageSaveError(null);
    setStageSaveBusy(true);
    setLostPending(false);
    try {
      if (isLead) {
        await patchCrmLead(entityId, { sales_stage: next });
      } else {
        const res = await patchCrmOrderSalesStage(entityId, next, {
          force,
          lost_reason: lost?.reason,
          lost_note: lost?.note,
          notify_client: opts?.notifyClient === true,
          client_message: opts?.clientMessage,
        });
        const saved = (res?.sales_stage as SalesStage | undefined) ?? next;
        stageCommitRef.current = saved;
        setStage(saved);
        setStageNotifyTarget(null);
        setPendingStage(null);
        const stored = loadCrmDrawerMacroState(dealStorageId);
        const opens = stored
          ? { sales: stored.sales ?? false, compose: stored.compose ?? false, ops: stored.ops ?? false }
          : defaultMacroOpen(saved, { productionRead: prodRead, isLead });
        setSalesOpen(opens.sales);
        setOpsOpen(opens.ops);
        if (!stored && salesStageUsesOpsMacro(saved)) {
          setSalesOpen(false);
          setOpsOpen(true);
          setActiveMacro('ops');
        }
        if (saved === 'paid') setHandoffHint(true);
        hapticSuccess();
        onUpdated();
        void invalidate();
        return;
      }
      setStage(next);
      setStageNotifyTarget(null);
      setPendingStage(null);
      if (next === 'paid') setHandoffHint(true);
      hapticSuccess();
      onUpdated();
      void invalidate();
    } catch (err: unknown) {
      const ax = err as {
        response?: { status?: number; data?: { detail?: string | { message?: string } } };
      };
      const detail = ax.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : detail?.message || formatAdminPanelError(err, 'Не удалось сохранить этап');
      if (!isLead && ax.response?.status === 409) {
        setStageNotifyTarget(null);
        triggerTaskGate(message, next);
      } else {
        setStageSaveError(message);
        haptic.error();
      }
    } finally {
      setStageSaveBusy(false);
    }
  };

  const convert = async () => {
    if (!isLead) return;
    hapticSelection();
    setConverting(true);
    setConvertError(null);
    try {
      const res = await convertCrmLead(lead!.id);
      if (res.bitrix_deal_id) setBitrixId(res.bitrix_deal_id);
      hapticSuccess();
      onUpdated();
      void invalidate();
      onClose();
    } catch (err: unknown) {
      setConvertError(formatAdminPanelError(err, 'Не удалось оформить заказ из лида'));
      haptic.error();
    } finally {
      setConverting(false);
    }
  };

  const assignManager = async (managerId: number | null) => {
    hapticSelection();
    if (isLead) {
      await patchCrmLead(entityId, { assigned_manager_id: managerId ?? undefined });
    } else {
      await assignAdminOrderManager(entityId, managerId);
    }
    onUpdated();
    void invalidate();
  };

  const addHandoff = async () => {
    if (isLead || !card) return;
    hapticSelection();
    await postCrmOrderActivity(card.id, {
      body: 'Заказ оплачен — передан в производство.',
      kind: 'handoff_to_production',
    });
    setHandoffHint(false);
    hapticSuccess();
    onUpdated();
  };

  const phone = isLead ? leadContact?.phone : orderContact?.phone ?? card?.user.phone;
  const telegramId = isLead ? lead!.telegram_id : card?.user.telegram_id;
  const username = !isLead ? card?.user.username ?? undefined : undefined;
  const userId = isLead ? lead!.user_id : card?.user.id;
  const showChat = !prodRead && Boolean(userId && telegramId);

  const navigatorLine = useMemo(
    () =>
      buildCrmNavigatorLine({
        isLead,
        stage,
        productionRead: prodRead,
        tasks,
        hasValidOpenTask: isLead ? Boolean(lead!.has_valid_open_task) : Boolean(card?.has_valid_open_task),
        hasOverdueTask: isLead ? Boolean(lead?.has_overdue_task) : Boolean(card?.has_overdue_task),
      }),
    [isLead, stage, prodRead, tasks, card, lead],
  );

  const stageControl = isLead ? (
    <CrmSalesStageGroups stage={stage} onSelect={requestStageChange} />
  ) : (
    <div className="space-y-3">
      <CrmOrderJourneyRail stage={stage} orderStatus={card?.status} />
      <p className="text-[10px] leading-snug px-1" style={{ color: 'var(--cyan)' }}>
        {journeyStep(stage).managerHint}
      </p>
      <CrmSalesStageIntro
        hasTelegram={Boolean(telegramId)}
        clientLabel={
          card!.user.first_name || card!.user.username
            ? `(${card!.user.first_name || `@${card!.user.username}`})`
            : undefined
        }
      />
      <CrmSalesStageGroups stage={stage} onSelect={requestStageChange} />
    </div>
  );

  const taskListBlock = (
    <>
      <div
        className="rounded-xl p-3"
        style={{
          background: taskGateHighlight ? 'rgba(252,129,74,0.12)' : 'rgba(246,173,85,0.08)',
          border: `1px solid ${taskGateHighlight ? 'rgba(252,129,74,0.5)' : 'rgba(246,173,85,0.25)'}`,
        }}
      >
        {taskError && (
          <div className="flex gap-2 mb-3 text-xs">
            <AlertTriangle size={16} className="shrink-0" style={{ color: '#FC814A' }} />
            <span style={{ color: '#FC814A' }}>{taskError}</span>
          </div>
        )}
        <p className="text-[11px] font-bold mb-2" style={{ color: '#F6AD55' }}>
          Новая задача
        </p>
        {isLead ? (
          <CrmTaskForm
            leadId={entityId}
            onCreated={() => {
              void loadTasks();
              setTaskGateHighlight(false);
              setTaskError(null);
            }}
            hapticSelection={hapticSelection}
          />
        ) : (
          <CrmTaskForm
            orderId={entityId}
            onCreated={() => {
              void loadTasks();
              setTaskGateHighlight(false);
              setTaskError(null);
            }}
            hapticSelection={hapticSelection}
          />
        )}
      </div>
      {tasksError && (
        <CrmInlineError message={tasksError} onRetry={() => void loadTasks()} />
      )}
      {loading ? (
        <p className="text-xs" style={{ color: 'var(--text-3)' }}>Загрузка задач…</p>
      ) : tasks.length === 0 && !tasksError ? (
        <p className="text-xs" style={{ color: 'var(--text-3)' }}>
          Нет открытых задач — создайте звонок или follow-up выше.
        </p>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => (
            <CrmTaskRow
              key={t.id}
              task={t}
              entityKind={isLead ? 'lead' : 'order'}
              entityId={entityId}
              managers={props.managers}
              onUpdated={() => {
                void loadTasks();
                setTaskGateHighlight(false);
                setTaskError(null);
                onUpdated();
              }}
              hapticSelection={hapticSelection}
              hapticSuccess={hapticSuccess}
            />
          ))}
        </div>
      )}
      {!isLead && crmFull && taskError && pendingStage && (
        <button
          type="button"
          className="mt-2 text-[10px] font-bold underline touch-manipulation"
          style={{ color: 'var(--text-3)' }}
          onClick={() => void applyStageChange(pendingStage, { force: true })}
        >
          Принудительно (admin)
        </button>
      )}
    </>
  );

  return (
    <AdminOverlayPortal zIndex={80}>
      <div className="flex flex-col h-[100dvh] max-h-[100dvh]">
      <div
        className="shrink-0 border-b"
        style={{ borderColor: 'var(--border-subtle)', background: 'rgba(10,25,47,0.92)' }}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <div className="min-w-0 flex-1 pr-2">
            <p className="text-sm font-bold truncate" style={{ color: 'var(--text-1)' }}>
              {isLead ? title : card!.order_number}
            </p>
            <p className="text-xs truncate" style={{ color: 'var(--text-3)' }}>{subtitle}</p>
            {!isLead && amountLabel && (
              <p className="text-sm font-bold mt-0.5" style={{ color: 'var(--cyan-bright)' }}>
                {amountLabel}
              </p>
            )}
            {navigatorLine && (
              <p
                className="text-[11px] mt-1.5 leading-snug"
                style={{ color: navigatorLineColor(navigatorLine.tone) }}
              >
                {navigatorLine.text}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="press p-2 rounded-full shrink-0 touch-manipulation min-h-[44px] min-w-[44px]"
            style={{ background: 'rgba(255,255,255,0.08)' }}
          >
            <X size={20} color="var(--text-2)" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 overscroll-contain min-h-0">
        {isLead && leadContact && (
          <CrmClientContactBlock
            contact={leadContact}
            telegramId={telegramId ?? undefined}
            hapticSelection={hapticSelection}
          />
        )}
        {!isLead && orderContact && card && (
          <CrmClientContactBlock
            contact={orderContact}
            deliveryAddress={card.delivery_address}
            userId={card.user.id}
            orderId={card.id}
            canEdit={!prodRead}
            telegramId={telegramId ?? undefined}
            username={username}
            hapticSelection={hapticSelection}
            hapticSuccess={hapticSuccess}
            onContactSaved={() => {
              onUpdated();
              void invalidate();
            }}
          />
        )}
        {!prodRead && !isLead && <CrmManagerGuide />}

        {!prodRead && (
          <CrmDealStepRail
            active={activeMacro}
            onSelect={goMacro}
            hideCompose={isLead}
            hideSales={prodRead}
            hapticSelection={hapticSelection}
          />
        )}

        {!prodRead && !isLead && (
          <CrmDealAccordion
            macroId="sales"
            title="Продажи и коммуникация"
            subtitle="Этап воронки, задачи, лента, чат"
            accent="#63B3ED"
            icon={<MessageCircle size={15} color="#63B3ED" />}
            open={salesOpen}
            onOpenChange={setSalesOpen}
            highlight={taskGateHighlight}
            defaultOpen={false}
          >
            <CrmDealSalesBody
              stageBlock={stageControl}
              handoffHint={
                handoffHint ? (
                  <div
                    className="p-3 rounded-xl flex gap-2 items-start"
                    style={{ background: 'rgba(159,122,234,0.12)', border: '1px solid rgba(159,122,234,0.35)' }}
                  >
                    <Factory size={16} color="#B794F4" className="shrink-0 mt-0.5" />
                    <button
                      type="button"
                      onClick={() => void addHandoff()}
                      className="press text-xs font-bold underline touch-manipulation"
                      style={{ color: '#63B3ED' }}
                    >
                      Добавить handoff в ленту
                    </button>
                  </div>
                ) : undefined
              }
              taskListBlock={taskListBlock}
              taskGateHighlight={taskGateHighlight}
              activityHighlight={activityHighlight}
              activityFeed={
                <CrmActivityFeed
                  orderId={entityId}
                  access={access}
                  productionMode={false}
                  hapticSelection={hapticSelection}
                />
              }
              chatAndManagerBlock={
                <>
                  {showChat && (
                    <CrmChatThread
                      userId={userId!}
                      orderId={entityId}
                      hapticSelection={hapticSelection}
                      onCreateLead={async () => {
                        hapticSelection();
                        await createCrmLead({
                          title: card!.user.first_name || card!.user.username || `Клиент ${card!.user.id}`,
                          telegram_id: card!.user.telegram_id ?? undefined,
                        });
                        void invalidate();
                      }}
                    />
                  )}
                  {crmFull && props.managers && props.managers.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase mb-1.5" style={{ color: 'var(--text-3)' }}>
                        Менеджер
                      </p>
                      <select
                        className="input-glass w-full text-sm min-h-[44px] touch-manipulation"
                        value={card!.assigned_manager_id ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          void assignManager(v ? Number(v) : null);
                        }}
                      >
                        <option value="">Не назначен</option>
                        {props.managers.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.first_name || m.username || m.id}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {bitrixId && (
                    <p
                      className="text-[10px] font-bold px-2 py-1.5 rounded-lg"
                      style={{ background: 'rgba(72,187,120,0.12)', color: '#68D391' }}
                    >
                      Bitrix · {bitrixId}
                    </p>
                  )}
                  {props.mode === 'order' && props.bitrixSyncError && (
                    <p
                      className="text-[10px] px-2 py-1.5 rounded-lg"
                      style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A' }}
                    >
                      Bitrix: {props.bitrixSyncError}
                    </p>
                  )}
                  {props.mode === 'order' && props.onNavigateClient360 && card!.user.id && (
                    <button
                      type="button"
                      onClick={() => {
                        hapticSelection();
                        props.onNavigateClient360!(card!.user.id);
                      }}
                      className="press w-full py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
                      style={{ background: 'rgba(159,122,234,0.12)', color: '#D6BCFA', border: '1px solid rgba(159,122,234,0.3)' }}
                    >
                      Клиент 360
                    </button>
                  )}
                </>
              }
            />
          </CrmDealAccordion>
        )}

        {!prodRead && isLead && (
          <CrmDealAccordion
            macroId="sales"
            title="Продажи и коммуникация"
            subtitle="Этап, задачи, лента"
            accent="#63B3ED"
            icon={<MessageCircle size={15} color="#63B3ED" />}
            open={salesOpen}
            onOpenChange={setSalesOpen}
            highlight={taskGateHighlight}
            defaultOpen={false}
          >
            <CrmDealSalesBody
              leadContextBlock={
                <>
                  {lead!.notes && (
                    <p
                      className="text-xs p-3 rounded-xl"
                      style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--text-2)' }}
                    >
                      {lead!.notes}
                    </p>
                  )}
                  {(lead!.source === 'measurement' || lead!.source === 'motor_quote') && lead!.source_ref_id && (
                    <button
                      type="button"
                      className="press w-full flex items-center gap-2 py-2.5 px-3 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
                      style={{ background: 'rgba(99,179,237,0.12)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.3)' }}
                      onClick={() => {
                        hapticSelection();
                        if (lead!.source === 'measurement' && props.onNavigateMeasurement) {
                          props.onNavigateMeasurement(lead!.source_ref_id!);
                        } else if (lead!.user_id && props.onNavigateClient360) {
                          props.onNavigateClient360(lead!.user_id);
                        }
                      }}
                    >
                      <ExternalLink size={14} />
                      {lead!.source === 'measurement'
                        ? `Замер #${lead!.source_ref_id}`
                        : `КП Motor #${lead!.source_ref_id}`}
                    </button>
                  )}
                  <CrmScheduleMeasurementBlock
                    leadId={entityId}
                    onScheduled={() => {
                      onUpdated();
                      void invalidate();
                    }}
                    onOpenMeasurement={props.mode === 'lead' ? props.onNavigateMeasurement : undefined}
                    hapticSelection={hapticSelection}
                    hapticSuccess={hapticSuccess}
                  />
                  <p className="text-[10px] leading-snug" style={{ color: 'var(--text-3)' }}>
                    Этап лида — без уведомления клиенту. После оформления заказа этапы и Telegram — в карточке
                    заказа.
                  </p>
                </>
              }
              stageBlock={stageControl}
              taskListBlock={taskListBlock}
              taskGateHighlight={taskGateHighlight}
              activityHighlight={activityHighlight}
              activityFeed={
                <CrmActivityFeed leadId={entityId} access={access} hapticSelection={hapticSelection} />
              }
              chatAndManagerBlock={
                <>
                  {showChat && <CrmChatThread userId={userId!} hapticSelection={hapticSelection} />}
                  {crmFull && props.managers && props.managers.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase mb-1.5" style={{ color: 'var(--text-3)' }}>
                        Менеджер
                      </p>
                      <select
                        className="input-glass w-full text-sm min-h-[44px] touch-manipulation"
                        value={lead!.assigned_manager_id ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          void assignManager(v ? Number(v) : null);
                        }}
                      >
                        <option value="">Не назначен</option>
                        {props.managers.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.first_name || m.username || m.id}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              }
            />
          </CrmDealAccordion>
        )}

        {!isLead && card && !prodRead && (
          <CrmDealAccordion
            macroId="compose"
            title="Состав и деньги"
            subtitle="Позиции, скидка, импорт из корзины"
            accent="#68D391"
            icon={<Package size={15} color="#68D391" />}
            open={composeOpen}
            onOpenChange={setComposeOpen}
            defaultOpen={false}
            badge={
              pricingDriftBadge ? (
                <span className="animate-pulse font-bold" style={{ color: '#FC814A' }}>
                  цены
                </span>
              ) : undefined
            }
            highlight={pricingDriftBadge}
          >
            <CrmOrderComposePanel
              key={`compose-${card.id}`}
              orderId={card.id}
              orderNumber={card.order_number}
              canEditItems={canEditOrderCompose}
              canEditPricing={canEditOrderPricing}
              hapticSelection={hapticSelection}
              hapticSuccess={hapticSuccess}
              onOpenCatalog={() => {
                hapticSelection();
                openOrderComposeCatalog(
                  navigate,
                  { orderId: card.id, orderNumber: card.order_number },
                  onClose,
                );
              }}
              onProductionHandoff={() => {
                setStage('production');
                setHandoffHint(false);
                setOpsOpen(true);
                onUpdated();
                void invalidate();
              }}
              salesStage={stage}
              hasClientTelegram={Boolean(card.user.telegram_id)}
              clientLabel={
                card.user.first_name || card.user.username
                  ? `(${card.user.first_name || `@${card.user.username}`})`
                  : undefined
              }
              onUpdated={() => {
                onUpdated();
                void invalidate();
              }}
            />
          </CrmDealAccordion>
        )}

        {!isLead && card && (
          <CrmDealAccordion
            macroId="ops"
            title="Операции и производство"
            subtitle="Замер, фото, статус заказа"
            accent={prodRead ? 'coral' : 'emerald'}
            icon={<Wrench size={15} color={prodRead ? '#FC814A' : '#68D391'} />}
            open={opsOpen}
            onOpenChange={setOpsOpen}
            defaultOpen={false}
          >
            <div className="space-y-3">
              {props.mode === 'order' && (props.canCancelOrder || props.crmFullControl) && (
                <CrmOrderStatusPanel
                  orderId={card.id}
                  orderNumber={card.order_number}
                  currentStatus={card.status || 'pending'}
                  hasTelegram={Boolean(card.user.telegram_id)}
                  disabled={prodRead}
                  onUpdated={() => {
                    onUpdated();
                    void invalidate();
                  }}
                  hapticSelection={hapticSelection}
                  hapticSuccess={hapticSuccess}
                />
              )}
              {props.mode === 'order' &&
                props.canCancelOrder &&
                props.onCancelOrder &&
                card.status !== 'cancelled' &&
                card.status !== 'delivered' && (
                  <button
                    type="button"
                    disabled={cancelling}
                    onClick={() => {
                      hapticSelection();
                      setCancelSheetOpen(true);
                    }}
                    className="press w-full py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px] disabled:opacity-50"
                    style={{
                      background: 'rgba(252,129,74,0.12)',
                      color: '#FC814A',
                      border: '1px solid rgba(252,129,74,0.35)',
                    }}
                  >
                    {cancelling ? 'Отмена…' : 'Отменить заказ'}
                  </button>
                )}
              <CrmOrderBoardPanel
                orderId={card.id}
                canEditMeasurement={canEditMeasurement}
                canEditItems={canEditOrderCompose}
                canEditPricing={canEditOrderPricing}
                opsOnly
                hapticSelection={hapticSelection}
                hapticSuccess={hapticSuccess}
                onOpenCatalog={() => {
                  hapticSelection();
                  openOrderComposeCatalog(
                    navigate,
                    { orderId: card.id, orderNumber: card.order_number },
                    onClose,
                  );
                }}
                onProductionHandoff={() => {
                  setStage('production');
                  setHandoffHint(false);
                  onUpdated();
                  void invalidate();
                }}
                salesStage={stage}
                onUpdated={() => {
                  onUpdated();
                  void invalidate();
                }}
              />
              {props.mode === 'order' && props.onOpenMeasurementsForUser && (
                <button
                  type="button"
                  onClick={() => {
                    hapticSelection();
                    props.onOpenMeasurementsForUser!(card.user.id);
                    onClose();
                  }}
                  className="press w-full py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
                  style={{ background: 'rgba(72,187,120,0.12)', color: '#68D391', border: '1px solid rgba(72,187,120,0.35)' }}
                >
                  Замеры клиента
                </button>
              )}
              {prodRead && (
                <CrmActivityFeed
                  orderId={entityId}
                  access={access}
                  productionMode
                  hapticSelection={hapticSelection}
                />
              )}
              {card.sales_stage_history && card.sales_stage_history.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-3)' }}>История этапов</p>
                  {card.sales_stage_history.map((ev, i) => (
                    <p
                      key={i}
                      className="text-[10px] p-2 rounded-lg"
                      style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--text-2)' }}
                    >
                      {(ev as { stage?: string }).stage} · {(ev as { at?: string }).at}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </CrmDealAccordion>
        )}
      </div>

      {!prodRead && (
        <CrmDealNextActionBar
          navigator={navigatorLine}
          stage={stage}
          phone={phone}
          productionRead={prodRead}
          hasOpenTask={hasOpenTask}
          nextOpenTaskKind={nextOpenTaskKind}
          onPlanCall={() => {
            setSalesOpen(true);
            goMacro('sales');
            focusTaskForm();
          }}
          onOpenOps={() => goMacro('ops')}
          onAfterCallClick={highlightActivityFeed}
          hapticSelection={hapticSelection}
        />
      )}

      {isLead && (
        <div className="shrink-0 px-4 py-3 border-t space-y-2" style={{ borderColor: 'var(--border-subtle)' }}>
          {convertError && <CrmInlineError message={convertError} />}
          <button
            type="button"
            disabled={converting}
            onClick={() => void convert()}
            className="press w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold min-h-[48px] touch-manipulation"
            style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }}
          >
            <ArrowRightCircle size={18} /> {converting ? '…' : 'Оформить заказ'}
          </button>
        </div>
      )}

      {stageNotifyTarget && !isLead && card && (
        <CrmStageNotifySheet
          orderNumber={card.order_number}
          stage={stageNotifyTarget}
          hasTelegram={Boolean(card.user.telegram_id)}
          saving={stageSaveBusy}
          error={stageSaveError}
          clientLabel={
            card.user.first_name || card.user.username
              ? `(${card.user.first_name || `@${card.user.username}`})`
              : undefined
          }
          onCancel={() => {
            if (stageSaveBusy) return;
            setStageNotifyTarget(null);
            setStageSaveError(null);
          }}
          onConfirm={({ notifyClient, clientMessage }) => {
            void applyStageChange(stageNotifyTarget, { notifyClient, clientMessage });
          }}
        />
      )}

      {lostPending && card && (
        <CrmLostReasonSheet
          orderNumber={card.order_number}
          onCancel={() => setLostPending(false)}
          onConfirm={(reason, note, notifyClient, clientMessage) => {
            void applyStageChange('lost', {
              lost: { reason, note },
              notifyClient,
              clientMessage,
            });
          }}
        />
      )}

      {cancelSheetOpen && !isLead && card && props.onCancelOrder && (
        <OrderStatusNotifySheet
          orderNumber={card.order_number}
          newStatus="cancelled"
          hasTelegram={Boolean(card.user.telegram_id)}
          initialNote={STATUS_NOTE_TEMPLATE.cancelled || ''}
          onCancel={() => {
            if (cancelling) return;
            setCancelSheetOpen(false);
          }}
          onConfirm={({ notifyClient, managerNote }) => {
            setCancelSheetOpen(false);
            void (async () => {
              setCancelling(true);
              try {
                const ok = await props.onCancelOrder!(card.id, { notifyClient, managerNote });
                if (ok) onClose();
              } finally {
                setCancelling(false);
              }
            })();
          }}
        />
      )}
      </div>
    </AdminOverlayPortal>
  );
}
